/**
 * Recipe cost rules shared by the stock tools: an item costs its stock recipe
 * lines x the CURRENT stock item costs. A cost that was never loaded is
 * unknown (null), never 0, and poisons every total that would include it.
 */
import type { IItem } from '@/interfaces/IItem';
import type { IOption } from '@/interfaces/IOption';
import type { IOptionItem } from '@/interfaces/IOptionItem';
import type { IStockItem } from '@/interfaces/IStockItem';
import type { IStockRecipe } from '@/interfaces/IStockRecipe';
import { roundPrice } from '@/utils';
import {
  activeItemOptions,
  activeOptionItems,
} from '../../menu/gpthelp-admin-menu-local';
import { optionItemPriceBeforeTax } from '../../menu/gpthelp-admin-menu-pricing';
import type { IGpthelpResolvedHiddenReason } from '../../menu/gpthelp-admin-menu-visibility';

export type ILiveRecipeLine = IStockRecipe & { stockItem: IStockItem };

export interface IGpthelpRecipeCostSummary {
  unitCost: number | null;
  unpricedStock: string[];
}

export interface IGpthelpVariantCost {
  optionItemId: number;
  title: string;
  price: number;
  unitCost: number | null;
  margin?: number;
  marginPct?: number;
}

export interface IGpthelpItemCostRow {
  itemId: number;
  name?: string;
  categoryName?: string;
  hiddenReasons?: IGpthelpResolvedHiddenReason[];
  price?: number;
  hasRecipe: boolean;
  unitCost?: number | null;
  unpricedStock?: string[];
  margin?: number;
  marginPct?: number;
  variants?: IGpthelpVariantCost[];
  optionRecipes?: true;
  description?: string;
  notInCatalog?: true;
}

type IOptionItemWithId = IOptionItem & { id: number };

const DESCRIPTION_MAX_CHARS = 200;

/** Stock costs of 0 or less were never loaded: unknown, not free. */
export function knownStockCost(cost: unknown): number | null {
  const value =
    typeof cost === 'number' || typeof cost === 'string' ? Number(cost) : NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function isConditionalRecipeLine(line: IStockRecipe): boolean {
  return line.optionId !== null || line.optionItemId !== null;
}

/** Drops lines whose stock item is gone (the sale deduction skips them too). */
export function liveRecipeLines(lines: IStockRecipe[]): ILiveRecipeLine[] {
  return lines.filter((line): line is ILiveRecipeLine => !!line.stockItem);
}

export function recipeLineCost(line: ILiveRecipeLine): number | null {
  const cost = knownStockCost(line.stockItem.cost);
  return cost === null ? null : roundPrice(Number(line.quantity) * cost);
}

/** No lines, or any unpriced line, makes the unit cost unknown. */
export function summarizeRecipeLines(
  lines: ILiveRecipeLine[]
): IGpthelpRecipeCostSummary {
  const unpriced = new Set<string>();
  let total = 0;
  for (const line of lines) {
    const cost = recipeLineCost(line);
    if (cost === null) unpriced.add(line.stockItem.name);
    else total = roundPrice(total + cost);
  }

  if (lines.length === 0 || unpriced.size > 0) {
    return { unitCost: null, unpricedStock: [...unpriced] };
  }
  return { unitCost: total, unpricedStock: [] };
}

function marginFields(
  price: number,
  unitCost: number | null
): { margin?: number; marginPct?: number } {
  if (unitCost === null) return {};
  const margin = roundPrice(price - unitCost);
  return price > 0
    ? { margin, marginPct: roundPrice((margin / price) * 100) }
    : { margin };
}

const hasId = (optionItem: IOptionItem): optionItem is IOptionItemWithId =>
  typeof optionItem.id === 'number';

function priceVariantChoices(options: IOption[]): IOptionItemWithId[] {
  for (const option of options) {
    if (!option.main) continue;
    const choices = activeOptionItems(option).filter(hasId);
    if (choices.length > 0) return choices;
  }
  return [];
}

function capDescription(description: string | null | undefined): string | null {
  const trimmed = (description ?? '').trim();
  if (trimmed === '') return null;
  return trimmed.length > DESCRIPTION_MAX_CHARS
    ? `${trimmed.slice(0, DESCRIPTION_MAX_CHARS - 1)}…`
    : trimmed;
}

function singlePriceCostFields(price: number, base: ILiveRecipeLine[]) {
  const { unitCost, unpricedStock } = summarizeRecipeLines(base);
  return {
    price,
    hasRecipe: base.length > 0,
    unitCost,
    ...(unpricedStock.length > 0 ? { unpricedStock } : {}),
    ...marginFields(price, unitCost),
  };
}

function variantCostFields(
  choices: IOptionItemWithId[],
  base: ILiveRecipeLine[],
  applicable: ILiveRecipeLine[]
) {
  const unpriced = new Set<string>();
  let anyVariantLines = false;

  const variants = choices.map(
    (choice): IGpthelpVariantCost => {
      const variantLines = applicable.filter(
        (line) => line.optionItemId === choice.id
      );
      if (variantLines.length > 0) anyVariantLines = true;

      const { unitCost, unpricedStock } = summarizeRecipeLines([
        ...base,
        ...variantLines,
      ]);
      unpricedStock.forEach((name) => unpriced.add(name));

      const price = optionItemPriceBeforeTax(choice);
      return {
        optionItemId: choice.id,
        title: choice.title ?? '',
        price,
        unitCost,
        ...marginFields(price, unitCost),
      };
    }
  );

  return {
    hasRecipe: base.length > 0 || anyVariantLines,
    ...(unpriced.size > 0 ? { unpricedStock: [...unpriced] } : {}),
    variants,
  };
}

/**
 * Cost row of a catalog item. `lines` may hold the whole page's lines: every
 * filter is keyed by this item's own id and option ids.
 */
export function buildItemCostRow(params: {
  item: IItem;
  price: number;
  categoryName: string | null;
  hiddenReasons?: IGpthelpResolvedHiddenReason[];
  lines: ILiveRecipeLine[];
}): IGpthelpItemCostRow {
  const { item, price, categoryName, hiddenReasons, lines } = params;

  const own = lines.filter((line) => line.itemId === item.id);
  const shared = lines.filter((line) => line.itemId === null);
  const base = own.filter((line) => !isConditionalRecipeLine(line));
  const applicable = [...own, ...shared];

  const options = activeItemOptions(item);
  const choices = priceVariantChoices(options);
  const extraOptionIds = new Set(
    options
      .filter((option) => !option.main)
      .map((option) => option.id)
      .filter((id): id is number => typeof id === 'number')
  );

  const costFields =
    choices.length > 0
      ? variantCostFields(choices, base, applicable)
      : singlePriceCostFields(price, base);
  const optionRecipes = applicable.some(
    (line) => line.optionId !== null && extraOptionIds.has(line.optionId)
  );
  const description = costFields.hasRecipe
    ? null
    : capDescription(item.description);

  return {
    itemId: item.id,
    name: item.name ?? '',
    ...(categoryName !== null ? { categoryName } : {}),
    ...(hiddenReasons && hiddenReasons.length > 0 ? { hiddenReasons } : {}),
    ...costFields,
    ...(optionRecipes ? { optionRecipes: true as const } : {}),
    ...(description !== null ? { description } : {}),
  };
}

/**
 * Deleted item still present in sales: costed from its base lines. Its menu
 * options are gone, so its own option lines can only be flagged.
 */
export function buildNotInCatalogCostRow(
  itemId: number,
  lines: ILiveRecipeLine[]
): IGpthelpItemCostRow {
  const own = lines.filter((line) => line.itemId === itemId);
  const base = own.filter((line) => !isConditionalRecipeLine(line));
  const { unitCost, unpricedStock } = summarizeRecipeLines(base);
  return {
    itemId,
    notInCatalog: true,
    hasRecipe: own.length > 0,
    unitCost,
    ...(unpricedStock.length > 0 ? { unpricedStock } : {}),
    ...(own.some(isConditionalRecipeLine)
      ? { optionRecipes: true as const }
      : {}),
  };
}

export function countUnknownCosts(rows: IGpthelpItemCostRow[]): number {
  return rows.reduce(
    (count, row) =>
      count +
      (row.unitCost === null ? 1 : 0) +
      (row.variants ?? []).filter((variant) => variant.unitCost === null)
        .length,
    0
  );
}

/**
 * Keeps rows in order while their serialized size fits the budget (the first
 * row always fits) and reports the ids of the rows left out.
 */
export function fitRowsToBudget<T extends { itemId: number }>(
  rows: T[],
  maxChars: number
): { kept: T[]; remainingItemIds: number[] } {
  let used = 0;
  let keptCount = 0;
  for (const row of rows) {
    const size = JSON.stringify(row).length + 1;
    if (keptCount > 0 && used + size > maxChars) break;
    used += size;
    keptCount++;
  }
  return {
    kept: rows.slice(0, keptCount),
    remainingItemIds: rows.slice(keptCount).map((row) => row.itemId),
  };
}
